import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { SplashScreen } from '~/modules/@shared/components/SplashScreen';
import { User } from '~/services/types';
import { useGetUserQuery } from '~/services/user';
import { QueryObserverResult } from '@tanstack/react-query';
import tracking from '~/services/tracking.ts';
import PendoService from '~/services/pendo';
import { errorReporting } from '~/services/logging';

type ContextAuth = {
  currentUser: User;
  isAuth: true;
  isLoading: boolean;
  refreshCurrentUser: QueryObserverResult['refetch'];
};
type ContextNoAuth = {
  currentUser: null;
  isAuth: false;
  isLoading: boolean;
  refreshCurrentUser: () => void;
};

type SessionContextType = ContextAuth | ContextNoAuth;

const SessionContext = createContext<SessionContextType>({
  currentUser: null,
  isLoading: true,
  isAuth: false,
  refreshCurrentUser: () => null,
});

export function SessionContextProvider({ children }: PropsWithChildren) {
  const { data: currentUser, isPending: isUserPending, isLoading, isFetching, refetch } = useGetUserQuery();

  const sessionValue = useMemo((): SessionContextType => {
    if (currentUser && !isUserPending) {
      tracking.setUserId(currentUser.id);
      errorReporting.logUser(currentUser);

      PendoService.identify(currentUser);

      return {
        currentUser,
        isLoading: isLoading || isFetching,
        isAuth: true,
        refreshCurrentUser: refetch,
      };
    }

    tracking.logoutUser();
    errorReporting.logoutUser();

    return {
      currentUser: null,
      isAuth: false,
      isLoading: isLoading || isFetching,
      refreshCurrentUser: () => null,
    };
  }, [currentUser, isLoading, isFetching, isUserPending]);

  if (!sessionValue.currentUser && (isLoading || isFetching)) {
    return <SplashScreen />;
  }

  return <SessionContext.Provider value={sessionValue}>{children}</SessionContext.Provider>;
}

export function useSessionContext() {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSessionContext must be used within a SessionContextProvider');
  }

  return context;
}
